import { Product } from '@gate31/types';
import ajaxAPI from 'ajaxAPI';
import Alpine, { AlpineComponent } from 'alpinejs';
import FavoritesProducts from 'FavoritesProducts';
import { WishlistItem } from '@gate31/types/wishlist';
import Favorite from '@gate31/uikit/common/components/favorite/favorite';

export interface FavoriteDeskComponent {
    products: Product[];
    isLoading: boolean;
    isError: boolean;
    readonly hasProducts: boolean;
    readonly getCount: number;

    init(): Promise<void>;
    removeProduct(productId: number): void;
    formatPrice(price: number | string): string;
    hasDiscount(product: Product): boolean;
    getDiscountPercent(product: Product): number;
    updateProductsData(wishlist: WishlistItem[]): Promise<void>;
}

export default (): AlpineComponent<FavoriteDeskComponent> => ({
    products: [],
    isLoading: true,
    isError: false,

    get hasProducts() {
        return this.products.length > 0;
    },

    get getCount() {
        return this.products.length;
    },

    async init() {
        try {
            const wishlistStore = Alpine.store('wishlist');

            await wishlistStore.sync();

            this.updateProductsData(Array.from(wishlistStore.wishlist.values()));

            // Need to think about types in $watch magic
            this.$watch('$store.wishlist.count', async (cur: number, prev: number) => {
                if (cur > prev) {
                    await this.updateProductsData(Array.from(wishlistStore.wishlist.values()));
                }
            });
        } catch (e) {
            this.isError = true;
        } finally {
            this.isLoading = false;
            FavoritesProducts.update();
            Favorite.initSlider();
        }
    },

    async updateProductsData(wishlist: WishlistItem[]) {
        const data: {} = await new Promise((res, rej) => {
            ajaxAPI.product.getList(Array.from(wishlist)
                .filter(item => ! item.hidden)
                .map(item => Number(item.id))).done(res).fail(rej);
        });

        this.products = Array.from(Object.values(data));
    },

    removeProduct(productId: number) {
        this.products = this.products.filter(p => p.id !== productId);
    },

    formatPrice(price: number | string) {
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

        if (isNaN(numericPrice)) return '';

        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'RUB',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(numericPrice);
    },

    hasDiscount(product: Product) {
        const variant = product.variants?.[0];

        if (! variant) return false;

        const price = Number(variant.price);
        const oldPrice = Number(variant.old_price) || 0;

        return oldPrice > price;
    },

    getDiscountPercent(product: Product) {
        if (this.hasDiscount(product)) {
            const variant = product.variants[0];
            const oldPrice = Number(variant.old_price);

            return Math.round((oldPrice - Number(variant.price)) / (oldPrice / 100));
        }
        return 0;
    }
});
